import type { Article, FaqItem, Service, Settings } from '@/types'
import { settings } from '@/content'

export const SITE_URL = (
  (import.meta.env.VITE_SITE_URL as string) || 'https://preprod.ateliers-du-terras.fr'
).replace(/\/$/, '')

export function absUrl(path: string): string {
  if (!path) return SITE_URL + '/'
  if (/^https?:\/\//.test(path)) return path
  return SITE_URL + (path.startsWith('/') ? path : '/' + path)
}

function clip(text?: string, max = 160): string {
  if (!text) return ''
  const t = text.replace(/\s+/g, ' ').trim()
  return t.length <= max ? t : t.slice(0, max - 1).trimEnd() + '…'
}

export interface HeadMeta {
  title: string
  description?: string
  canonical: string
  index?: boolean
  image?: string
  type?: 'website' | 'article'
  publishedTime?: string
  modifiedTime?: string
}

/** Balises head communes : title, description, canonical, robots, Open Graph, Twitter. */
export function baseHead(m: HeadMeta) {
  const url = absUrl(m.canonical)
  const image = m.image ? absUrl(m.image) : absUrl(settings.logo)
  const robots =
    m.index === false ? 'noindex,nofollow' : 'index,follow,max-image-preview:large'
  const description = clip(m.description)
  const meta: Record<string, string>[] = [
    { name: 'description', content: description },
    { name: 'robots', content: robots },
    { property: 'og:type', content: m.type || 'website' },
    { property: 'og:site_name', content: settings.site_name },
    { property: 'og:locale', content: 'fr_FR' },
    { property: 'og:title', content: m.title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: url },
    { property: 'og:image', content: image },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: m.title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: image },
  ]
  if (m.publishedTime) meta.push({ property: 'article:published_time', content: m.publishedTime })
  if (m.modifiedTime) meta.push({ property: 'article:modified_time', content: m.modifiedTime })
  return {
    title: m.title,
    meta,
    link: [{ rel: 'canonical', href: url }],
  }
}

function ld(obj: unknown) {
  return { type: 'application/ld+json', children: JSON.stringify(obj) }
}

/** LocalBusiness global (present sur toutes les pages). */
export function localBusinessLd(s: Settings = settings) {
  const locations = s.agencies.map((a) => ({
    '@type': 'LocalBusiness',
    name: `${s.site_name} - ${a.city}`,
    image: absUrl(s.logo),
    telephone: a.phone_tel,
    address: {
      '@type': 'PostalAddress',
      streetAddress: a.street,
      postalCode: a.zip,
      addressLocality: a.town,
      addressCountry: 'FR',
    },
    geo: a.geo ? { '@type': 'GeoCoordinates', latitude: a.geo.lat, longitude: a.geo.lng } : undefined,
    openingHours: 'Mo-Fr 08:00-12:00,13:30-18:00',
  }))
  return ld({
    '@context': 'https://schema.org',
    '@type': 'GeneralContractor',
    '@id': SITE_URL + '/#business',
    name: s.site_name,
    url: SITE_URL + '/',
    logo: absUrl(s.logo),
    image: absUrl(s.logo),
    telephone: s.phones[0]?.tel,
    email: s.email,
    priceRange: '€€',
    foundingDate: s.since,
    areaServed: s.zones,
    sameAs: [s.socials.facebook, s.socials.instagram, s.socials.linkedin],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: s.rating.value,
      reviewCount: s.rating.count,
      bestRating: '5',
    },
    location: locations,
    address: {
      '@type': 'PostalAddress',
      streetAddress: s.agencies[0].street,
      postalCode: s.agencies[0].zip,
      addressLocality: s.agencies[0].town,
      addressCountry: 'FR',
    },
  })
}

export function breadcrumbLd(crumbs: { label: string; path: string }[]) {
  return ld({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.label,
      item: absUrl(c.path),
    })),
  })
}

export function serviceLd(service: Service) {
  return ld({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.h1 || service.title,
    description: clip(service.seo?.seo_description || service.intro, 300),
    url: absUrl(service.path),
    image: service.seo?.og_image ? absUrl(service.seo.og_image) : undefined,
    areaServed: settings.zones,
    provider: { '@id': SITE_URL + '/#business' },
  })
}

export function faqLd(faq: FaqItem[]) {
  return ld({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  })
}

export function articleLd(article: Article, authorName?: string) {
  return ld({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: clip(article.seo?.seo_description || article.excerpt, 300),
    image: article.featured_image ? absUrl(article.featured_image) : undefined,
    datePublished: article.date,
    dateModified: article.modified || article.date,
    author: authorName ? { '@type': 'Person', name: authorName } : { '@id': SITE_URL + '/#business' },
    publisher: { '@id': SITE_URL + '/#business' },
    mainEntityOfPage: absUrl(article.path),
  })
}

export { ld }
